import { useEffect, useMemo, useRef, useState } from "react";

import { getColumnSizeVariables } from "./helper";

export function useColumnSizeVariables({ tableRef, tableContext }) {
  const table = tableContext()
  const { columnSizing } = table.getState()
  const hasManualSizing = Object.keys(columnSizing).length > 0
  const [containerWidth, setContainerWidth] = useState(0)

  useEffect(() => {
    if (!tableRef.current) {
      return undefined
    }

    const resizeObserver = new ResizeObserver(entries => {
      const [entry] = entries
      setContainerWidth(entry?.contentRect?.width ?? 0)
    })

    resizeObserver.observe(tableRef.current)
    return () => resizeObserver.disconnect()
  }, [tableRef])

  return useMemo(() => getColumnSizeVariables({
    table,
    columnSizing,
    hasManualSizing,
    containerWidth,
  }), [columnSizing, containerWidth, table, hasManualSizing])
}

export function useAutoResize({ tableRef, tableContext, columns }) {
  const table = tableContext()
  const { isFixedLayout, setIsFixedLayout } = table

  const seededSizingKeyRef = useRef('')
  const columnSizingSeedKey = useMemo(() => columns.map((column, index) => [
    column.id || column.dataField || `column-${index}`,
    column.size,
    column.maxSize,
    column.enableResizing ?? column.enableColumnResize ?? true,
  ].join(':')).join('|'), [columns])

  useEffect(() => {
    const keyChanged = seededSizingKeyRef.current !== columnSizingSeedKey
    if (keyChanged) {
      seededSizingKeyRef.current = columnSizingSeedKey
      setIsFixedLayout(false)
      table.setColumnSizing({})
      return undefined
    }

    if (isFixedLayout || !tableRef.current) {
      return undefined
    }

    const frameId = requestAnimationFrame(() => {
      const measuredById = {}
      tableRef.current.querySelectorAll('[data-col-id]').forEach(element => {
        const columnId = element.getAttribute('data-col-id')
        if (!columnId) {
          return
        }
        measuredById[columnId] = Math.max(measuredById[columnId] || 0, Math.ceil(element.getBoundingClientRect().width))
      })

      const nextColumnSizing = {}
      table.getAllLeafColumns().forEach(column => {
        const explicitSize = column.columnDef.size
        const hasExplicitSize = column.columnDef.meta?.hasExplicitSize
        const measured = measuredById[column.id]
        const baseSize = hasExplicitSize ? explicitSize : typeof measured === 'number' ? measured : column.getSize()
        const { maxSize } = column.columnDef
        nextColumnSizing[column.id] = typeof maxSize === 'number' ? Math.min(maxSize, baseSize) : baseSize
      })

      table.setColumnSizing(nextColumnSizing)
      setIsFixedLayout(true)
    })

    return () => cancelAnimationFrame(frameId)
  }, [columnSizingSeedKey, isFixedLayout, table, columns, tableRef, setIsFixedLayout])

  return isFixedLayout
}
